import React, { useEffect, useState } from "react";
import { useDashboard } from "../context/DashboardContext";
import { PageHeader } from "../components/common/PageParts";
import { Bell, BellRing, Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { fmtPrice } from "../lib/format";
import {
  getAlerts, addAlert, removeAlert, onAlertsChanged,
  notifyPermission, requestNotifyPermission,
} from "../lib/alerts";

export default function Alerts() {
  const { pairs, activeSymbol, livePrices } = useDashboard();
  const [alerts, setAlerts] = useState(getAlerts());
  const [form, setForm] = useState({ symbol: activeSymbol, cond: "above", price: "" });
  const [perm, setPerm] = useState(notifyPermission());

  useEffect(() => { setForm((f) => ({ ...f, symbol: activeSymbol })); }, [activeSymbol]);
  useEffect(() => onAlertsChanged(() => setAlerts(getAlerts())), []);

  const add = () => {
    if (!form.price) { toast.error("Enter a target price"); return; }
    addAlert({ id: Date.now().toString(), symbol: form.symbol, cond: form.cond, price: Number(form.price), triggered: false });
    setForm({ ...form, price: "" });
    toast.success("Alert created");
  };

  const enableNotifications = async () => {
    const p = await requestNotifyPermission();
    setPerm(p);
    if (p === "granted") toast.success("Browser notifications enabled");
    else if (p === "denied") toast.error("Notifications blocked in browser settings");
    else if (p === "unsupported") toast.error("This browser doesn't support notifications");
  };

  return (
    <div className="ym-fade">
      <PageHeader title="Alerts" subtitle="Live price alerts checked against the streaming feed — fire in-app + browser notifications"
        right={
          <div className="flex items-center gap-2">
            {perm === "granted" ? (
              <span className="pill" style={{ color: "var(--green)", background: "rgba(34,197,94,.12)", borderColor: "transparent" }}><BellRing size={13} /> Notifications On</span>
            ) : (
              <button onClick={enableNotifications} className="pill ym-hover" style={{ color: "var(--accent-soft)", background: "rgba(139,92,246,.12)", borderColor: "rgba(139,92,246,.3)" }}>
                <Bell size={13} /> Enable Notifications
              </button>
            )}
            <span className="pill" style={{ color: "var(--text-muted)", background: "var(--panel-2)", borderColor: "var(--border)" }}>{alerts.length}</span>
          </div>
        } />

      <div className="ym-panel p-4 mb-4 flex flex-wrap items-end gap-3">
        <select value={form.symbol} onChange={(e) => setForm({ ...form, symbol: e.target.value })} className="rounded-lg px-3 py-2 text-[13px] outline-none" style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }}>
          {pairs.map((p) => <option key={p.id} value={p.symbol}>{p.symbol}</option>)}
        </select>
        <div className="flex gap-1">
          {["above", "below"].map((c) => (
            <button key={c} onClick={() => setForm({ ...form, cond: c })} className="px-3 py-2 rounded-lg text-[12px] font-semibold ym-hover flex items-center gap-1"
              style={{ background: form.cond === c ? "rgba(139,92,246,.2)" : "var(--panel-2)", color: form.cond === c ? "#fff" : "var(--text-muted)", border: "1px solid var(--border)" }}>
              {c === "above" ? <ArrowUp size={13} /> : <ArrowDown size={13} />} {c}
            </button>
          ))}
        </div>
        <input placeholder="Target price" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} type="number" className="w-36 rounded-lg px-3 py-2 text-[13px] tabnum outline-none" style={{ background: "var(--panel-2)", border: "1px solid var(--border)", color: "var(--text)" }} />
        <button onClick={add} className="flex items-center gap-1.5 rounded-lg px-5 py-2 text-[13px] font-semibold ym-hover" style={{ background: "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff" }}><Plus size={15} /> Create</button>
      </div>

      <div className="space-y-2">
        {alerts.map((a) => {
          const cur = livePrices?.[a.symbol]?.price;
          return (
            <div key={a.id} className="ym-card p-3.5 flex items-center gap-3">
              <span className="font-bold text-[13px]">{a.symbol}</span>
              <span className="pill" style={{ color: a.cond === "above" ? "var(--green)" : "var(--red)", background: "transparent", borderColor: "var(--border)" }}>{a.cond} ${fmtPrice(a.price)}</span>
              <span className="text-[12px] tabnum" style={{ color: "var(--text-muted)" }}>now ${cur ? fmtPrice(cur) : "…"}</span>
              {a.triggered && <span className="pill" style={{ color: "var(--amber)", background: "rgba(245,158,11,.12)", borderColor: "transparent" }}>Triggered</span>}
              <button onClick={() => removeAlert(a.id)} className="ml-auto grid place-items-center rounded-md ym-hover" style={{ width: 26, height: 26, background: "var(--panel-2)", border: "1px solid var(--border)" }}><Trash2 size={13} style={{ color: "var(--red)" }} /></button>
            </div>
          );
        })}
        {!alerts.length && <div className="ym-card p-8 text-center text-[12px]" style={{ color: "var(--text-dim)" }}>No alerts yet. Create one above — it will fire even while you browse other pages.</div>}
      </div>
    </div>
  );
}
